import type { HydratedImage, ParameterGroup } from "../generated/api/types.gen";
import { api } from "../lib/api-client";
import type {
  ParametersUpdatedEvent,
  RefinementStreamEvent,
} from "./frontend-types";
import { isParametersUpdatedEvent } from "./frontend-types";

export type { ParametersUpdatedEvent, RefinementStreamEvent };
export { isParametersUpdatedEvent };

interface RegenerationStreamParams {
  usePromptRefinement: boolean;
  requestId: string;
  originalImageId: string;
  promptDirty: boolean;
  promptText: string;
  originalPrompt: string;
  currentParams: {
    parameter_groups: ParameterGroup[];
    referenceImages: string[];
  };
}

export function getRegenerationStream({
  usePromptRefinement,
  requestId,
  originalImageId,
  promptDirty,
  promptText,
  originalPrompt,
  currentParams,
}: RegenerationStreamParams): AsyncGenerator<
  RefinementStreamEvent,
  void,
  unknown
> {
  if (usePromptRefinement) {
    const promptToUse =
      promptDirty && promptText.trim() ? promptText.trim() : originalPrompt;
    const referenceImagesForApi = currentParams.referenceImages
      ?.map((ref) => (ref.includes(",") ? ref.split(",")[1] : ref))
      .filter(Boolean);
    return api.generation.regenerateWithPromptStream(
      originalImageId,
      {
        prompt: promptToUse,
        reference_images: referenceImagesForApi?.length
          ? referenceImagesForApi
          : undefined,
      },
      requestId,
    );
  }
  return api.generation.regenerateWithAttributesStream(
    originalImageId,
    { parameter_groups: currentParams.parameter_groups || [] },
    requestId,
  );
}

interface ProcessStreamParams {
  stream: AsyncGenerator<RefinementStreamEvent, void, unknown>;
  originalImageId: string;
  refinementImage: HydratedImage | null;
  onImageUpdate: (image: HydratedImage) => void;
  reset: (values: { parameter_groups: ParameterGroup[] }) => void;
  /** Fired once, on the first image event. The parameters_updated follow-up
   * can lag by a vision call, so navigation must not wait for stream end. */
  onImageReady?: (image: HydratedImage) => Promise<void> | void;
}

export async function processRegenerationStream({
  stream,
  originalImageId,
  refinementImage,
  onImageUpdate,
  reset,
  onImageReady,
}: ProcessStreamParams): Promise<HydratedImage | null> {
  let lastImage: HydratedImage | null = null;
  let imageReadyFired = false;
  for await (const event of stream) {
    if (isParametersUpdatedEvent(event)) {
      const current: HydratedImage | null = lastImage;
      if (current && event.image_id === current.id) {
        const refreshed: HydratedImage = {
          ...current,
          parameter_groups: event.parameter_groups,
        };
        onImageUpdate(refreshed);
        reset({ parameter_groups: event.parameter_groups });
        lastImage = refreshed;
      }
      continue;
    }
    const originalGroups = refinementImage?.parameter_groups;
    const finalGroups =
      event?.parameter_groups && event.parameter_groups.length > 0
        ? event.parameter_groups
        : originalGroups;
    const enriched: HydratedImage = {
      ...event,
      parameter_groups: finalGroups,
      original_id: originalImageId,
    };
    onImageUpdate(enriched);
    if (finalGroups) reset({ parameter_groups: finalGroups });
    lastImage = enriched;
    if (!imageReadyFired && onImageReady) {
      imageReadyFired = true;
      await onImageReady(enriched);
    }
  }
  return lastImage;
}
